import {defaultIBadgeGroup, IBadgeGroup} from "shared/IBadgeGroup";
import {DateTime} from "luxon";
import {DashboardSolution, IBadgeType} from "data/entities/DashboardSolution";
import {DateTimeHelpers} from "shared/DateTimeHelpers";
import {UIState} from "data/UIState";
import DashboardData from "data/DashboardData";

const MAX_SINGULAR_BADGES = 6;
const UNCATEGORIZED_BADGE_GROUP_ID = "uncategorized-badge-group";

export interface IBadgeCount {
  count:number;
  badgeType:IBadgeType;
}

export class BadgeGroup implements IBadgeGroup {
  _id;
  startDate;
  endDate;
  pointGoal;
  desc;
  hidden;
  name;

  startDateTime:DateTime;
  endDateTime:DateTime;

  badgedDashboardSolutions:DashboardSolution[] = [];
  badges:number = 0;
  points:number = 0;

  badgeCounts:IBadgeCount[] = [];

  constructor(json?:IBadgeGroup) {
    if(json) {
      this._id = json._id;
      this.startDate = json.startDate;
      this.endDate = json.endDate;
      this.pointGoal = json.pointGoal;
      this.desc = json.desc;
      this.hidden = json.hidden;
      this.name = json.name;
    }
  }

  add = (dashboardSolution:DashboardSolution) => {
    this.badgedDashboardSolutions.push(dashboardSolution);
    this.badges++;
    this.points+=dashboardSolution.points;
    this.addToCounts(dashboardSolution);
  }

  checkAndAddByBadgeGroupId = (dashboardSolution:DashboardSolution) => {
    if(dashboardSolution.badgeGroupId===this._id) {
      this.add(dashboardSolution);
      return true;
    }
    return false;
  }

  checkAndAddByDate = (dashboardSolution:DashboardSolution) => {
    const badgeEarnedDate = DateTimeHelpers.parseDateTime(dashboardSolution.badgeEarnedDate);
    if(null!==badgeEarnedDate) {
      if(this.startDateTime<badgeEarnedDate && this.endDateTime>badgeEarnedDate) {
        this.add(dashboardSolution);
        return true;
      }
    }
    return false;
  }

  addToCounts = (dashboardSolution:DashboardSolution) => {

    // reached 7, now compress the badge groups
    if((MAX_SINGULAR_BADGES+1)==this.badgedDashboardSolutions.length) {
      const compressedBadgeCounts:IBadgeCount[] = [{ count:1, badgeType:this.badgeCounts[0].badgeType}];
      for(let i=1;i<this.badgeCounts.length;i++) {
        if(this.badgeCounts[i].badgeType.id===compressedBadgeCounts[compressedBadgeCounts.length-1].badgeType.id) {
          compressedBadgeCounts[compressedBadgeCounts.length-1].count++;
        } else {
          compressedBadgeCounts.push({
            count:1,
            badgeType:this.badgeCounts[i].badgeType,
          });
        }
      }
      this.badgeCounts = compressedBadgeCounts;
    }


    // singular
    if(MAX_SINGULAR_BADGES>=this.badgedDashboardSolutions.length) {
      this.badgeCounts.push({
        count:1,
        badgeType:dashboardSolution.badgeType,
      });
      this.badgeCounts.sort((a, b) => a.badgeType.label.localeCompare( b.badgeType.label ));

    }
    // compressed
    else {
      let found = false;
      for(let i=0;i<this.badgeCounts.length;i++) {
        if(this.badgeCounts[i].badgeType.id===dashboardSolution.badgeType.id) {
          this.badgeCounts[i].count++;
          found = true;
          break;
        }
      }
      if(!found) {
        this.badgeCounts.push({
          count: 1,
          badgeType: dashboardSolution.badgeType,
        });
      }

      this.badgeCounts.sort((a, b) => b.count - a.count);
    }
  }


}

/**
 * When Badges are specified
 *
 * @param configuredBadgeGroups
 * @param badgedDashboardSolutions
 */
const badgeGroups = (configuredBadgeGroups:IBadgeGroup[], badgedDashboardSolutions:DashboardSolution[]) => {
  const badgeGroups:BadgeGroup[] = configuredBadgeGroups.map(obj => new BadgeGroup(obj));
  const uncategorizedBadgedDashboardSolutions:DashboardSolution[] = [];

  function insertBadgedSolution(dashboardSolution:DashboardSolution) {
    if(badgeGroups.length>0) {
      for(let i=0; i<badgeGroups.length; i++) {
        if(badgeGroups[i].checkAndAddByBadgeGroupId(dashboardSolution)){
          return;
        }
      }
    }
    uncategorizedBadgedDashboardSolutions.push(dashboardSolution);
  }

  for(let i=0; i<badgedDashboardSolutions.length; i++) {
    insertBadgedSolution(badgedDashboardSolutions[i]);
  }

  if(0!==uncategorizedBadgedDashboardSolutions.length) {
    const uncategorizedBadgeGroup = new BadgeGroup();
    uncategorizedBadgeGroup._id = UNCATEGORIZED_BADGE_GROUP_ID;
    uncategorizedBadgeGroup.name = "Uncategorized Badges";
    uncategorizedBadgedDashboardSolutions.forEach(value => { uncategorizedBadgeGroup.add(value); });
    badgeGroups.push(uncategorizedBadgeGroup);
  }

  return badgeGroups;
}



/**
 * Used to create a badge group for each year
 *
 * @param badgedDashboardSolutions
 */
const badgeGroupsByYear = (badgedDashboardSolutions:DashboardSolution[]) =>  {
  const badgeGroups:BadgeGroup[] = [];

  function badgeGroupForDashboardSolutionYear(dashboardSolution:DashboardSolution){
    const badgeGroup = new BadgeGroup();
    badgeGroup._id = dashboardSolution.badgeGroupId;
    badgeGroup.name = badgeGroup._id;
    const badgeEarnedDate = DateTimeHelpers.parseDateTimeOr(dashboardSolution.badgeEarnedDate, DateTime.now());
    badgeGroup.startDateTime = badgeEarnedDate.startOf('year');
    badgeGroup.endDateTime = badgeEarnedDate.endOf('year');
    badgeGroup.add(dashboardSolution);
    return badgeGroup;
  }

  function insertBadgedSolution(dashboardSolution:DashboardSolution) {
    if(badgeGroups.length>0) {
      for(let i=0; i<badgeGroups.length; i++) {
        if(badgeGroups[i].checkAndAddByDate(dashboardSolution)){
          return;
        }
      }
    }
    badgeGroups.push(badgeGroupForDashboardSolutionYear(dashboardSolution));
  }

  for(let i=0; i<badgedDashboardSolutions.length; i++) {
    insertBadgedSolution(badgedDashboardSolutions[i]);
  }

  const now = DateTime.now();
  if(0===badgeGroups.length || ""+now.year!==badgeGroups[0]._id) {
    const nowGroup = new BadgeGroup(defaultIBadgeGroup());
    nowGroup._id = ""+now.year;
    nowGroup.name = nowGroup._id;
    nowGroup.startDateTime = now.startOf('year');
    nowGroup.endDateTime = now.endOf('year');
    badgeGroups.splice(0,0, nowGroup);
  }

  return badgeGroups;
}


/**
 * Initialize Badge Groups
 *
 * @param badgedDashboardSolutions
 */
export const generateBadgeGroups = (badgedDashboardSolutions:DashboardSolution[]) => {
  if (UIState.settings.USES_BADGE_GROUPS) {
    DateTimeHelpers.sortObjectsByProperty(DashboardData.badgeGroups, "startDate", true);
    return badgeGroups(DashboardData.badgeGroups, badgedDashboardSolutions);
  } else {
    DateTimeHelpers.sortObjectsByProperty(badgedDashboardSolutions, "badgeEarnedDate", true);
    return badgeGroupsByYear(badgedDashboardSolutions);
  }
}

